import { useMemo } from "react";

import { useGetEnabledIntegrations } from "@/api/integration/useGetEnabledIntegrations";
import { useGetOrganizations } from "@/api/organization/useGetOrganizations";
import { useGetProjectsByOrganization } from "@/api/project/useGetProjectsByOrganization";
import { useActiveOrganizationUuid } from "@/hooks/use-active-organization-uuid";
import { useAuth } from "@/hooks/use-auth";
import type { IGlobalSearchOrganizationInputs } from "@/lib/global-search/types";

import type { IUseGlobalSearchOrganizationInputsProps } from "../types";

// NOTE: NSAI-6589 — ownership and the per-project permissions of the active
// project's organization, the inputs of every ORG_* gate. Fetched only while the
// palette is open (and never in a personal project), from the same queries the
// organization settings shell uses, so opening settings afterwards is a cache
// hit rather than a second request.
export const useGlobalSearchOrganizationInputs = ({
  isEnabled,
}: IUseGlobalSearchOrganizationInputsProps): IGlobalSearchOrganizationInputs => {
  const { isPersonalProject } = useAuth();
  const activeOrganizationUuid = useActiveOrganizationUuid();
  const isOrganizationQueryEnabled = isEnabled && !!activeOrganizationUuid;

  const { data: organizations } = useGetOrganizations(
    isOrganizationQueryEnabled,
  );
  const { data: organizationProjects } = useGetProjectsByOrganization(
    isOrganizationQueryEnabled
      ? (activeOrganizationUuid ?? undefined)
      : undefined,
  );
  const { data: enabledIntegrations, isSuccess: isEnabledListResolved } =
    useGetEnabledIntegrations(isEnabled);

  const isOrganizationOwner = useMemo(
    () =>
      organizations?.find(
        (organization) => organization.uuid === activeOrganizationUuid,
      )?.isOwner ?? false,
    [organizations, activeOrganizationUuid],
  );

  return useMemo(
    () => ({
      isPersonalProject,
      isOrganizationOwner,
      organizationProjects: organizationProjects ?? [],
      enabledIntegrations: isEnabledListResolved ? enabledIntegrations : null,
    }),
    [
      isPersonalProject,
      isOrganizationOwner,
      organizationProjects,
      isEnabledListResolved,
      enabledIntegrations,
    ],
  );
};
